import { Tab, TabList, Tabs } from '@clay-run/glaze/tabs';
import { SUBTABS, type Subtab } from '../../../../../../mini-apps/command-center/subtabs';
import { GlobalSearch } from '../../search/GlobalSearch';
import { PanelToggles } from './PanelToggles';
import { usePanelTogglesHost } from './panels/usePanelTogglesHost';

// The design's own tab labels (Open Question 2's default) — `subtabs.ts`'s
// `?tab=` values (account/contact/plays/signals) are the wire contract
// (flow-links.ts) and stay unrenamed;
// only what's PAINTED here changes. The old shell keeps `VISIBLE_SUBTABS`'s
// own `label` ("Accounts"/"Contacts") until it converts.
const HEADER_TAB_LABELS: Record<Subtab, string> = {
  account: 'Companies',
  contact: 'People',
  plays: 'Plays',
  signals: 'Signals',
};

/**
 * The search wrapper's two class sets (Spencer QA round 3) — complete
 * literal strings, picked by `hostsToggles` below, because Tailwind can only
 * scan whole candidates. Both: `ml-auto` pushes the search (and the toggles
 * after it) to the row's right edge once the search has hit its cap;
 * `grow basis-0 min-w-0 max-w-80` lets it take up to 320px and SHRINK below
 * that instead of wrapping (hypothetical main size 0 ⇒ it never wraps on
 * its own). Longhands, not `flex-1`: whether `basis-full` beats the `flex`
 * shorthand depends on Tailwind's property order, whereas `basis-0` vs the
 * variant's `basis-full` is one longhand and the variant wins outright.
 * Below the container threshold the search drops to its own full-width row
 * exactly as in round 2 (`order-last basis-full max-w-none pb-2` —
 * `order-last` is what keeps the toggles on row one). Two thresholds
 * because the fixed content differs by the 92px cluster + 12px gap: see
 * "Wrapping (R11)" in the Header doc below for the arithmetic.
 */
const SEARCH_WRAPPER_BESIDE_TOGGLES =
  'ml-auto min-w-0 grow basis-0 max-w-80 @max-[44rem]:order-last @max-[44rem]:basis-full @max-[44rem]:max-w-none @max-[44rem]:pb-2';
const SEARCH_WRAPPER_ALONE =
  'ml-auto min-w-0 grow basis-0 max-w-80 @max-[36rem]:order-last @max-[36rem]:basis-full @max-[36rem]:max-w-none @max-[36rem]:pb-2';

/**
 * State the Header needs (destination tabs, the omnibox) that
 * `useCommandCenterSubtab` + the omnibox select handlers otherwise only exist
 * inside the routed command-center content — the Header renders ABOVE
 * `<Routes>` (hb's lens grammar: the header row never unmounts, even for a
 * drill-down), so `FlowShell` lifts this state and passes it down as props.
 */
interface HeaderBaseProps {
  /** Optional — `PlainHeader` (off-Flow) passes none. */
  initialQuery?: string | null;
  onSelectAccount: (accountId: string) => void;
  onSelectContact: (contactId: string, accountId: string | null) => void;
}

/** The tab group is all-or-nothing: with it the header paints `Tabs`, without it none. */
type HeaderTabProps =
  | {
      subtab: Subtab;
      setSubtab: (t: Subtab) => void;
      /** Tabs left out of the bar (Signals for the GS role). Their `?tab=` links still work. */
      hiddenTabs?: readonly Subtab[];
    }
  | { subtab?: undefined; setSubtab?: undefined; hiddenTabs?: undefined };

export type HeaderProps = HeaderBaseProps & HeaderTabProps;

/**
 * The new shell's 48px header row — hb `shell/Header.tsx`'s destination
 * `Tabs` + search + rail-toggle grammar, using the design's own labels
 * (Companies/People/Plays/Signals — Open Question 2's default; the `?tab=`
 * values underneath stay `account`/`contact`/`plays`/`signals`, unrenamed —
 * `subtabs.ts`'s own doc comment). Phase 1 embeds Terra's real `GlobalSearch`
 * omnibox in hb's search slot as-is (hb's own `DebouncedSearchInput` is a
 * per-tab row filter with no Terra counterpart yet). Where hb's single rail
 * toggle sat is `PanelToggles` (`./PanelToggles.tsx`) — the three mode
 * toggles, shared with `rail/RailDock.tsx`. Which host renders them is
 * `derivePanelTogglesHost` (`panels/flow-panels-layout.ts`): this header
 * while it IS the shell's top-right corner (Actions closed, or overlay
 * mode), the Actions strip whenever the Actions column is open in push mode;
 * see `PanelToggles.tsx`'s header for the one-visible-place rule. The
 * Actions column's content is
 * `RailDock`'s real enroll-queue dock, portaled in (plan 005 R8) and kept in
 * lockstep with the Actions toggle by `useActionsDockSync`
 * (`panels/useActionsDockSync.ts`, mounted in `RailDock`) — so the toggle
 * only needs to flip the panel store; it no longer also has to drive the
 * dock's own open state directly.
 *
 * Maps all four of `SUBTABS` — hb `shell/Header.tsx:11-16` shows every
 * destination (plan 005 §0.4) — minus `hiddenTabs`: the GS role drops
 * Signals (`showsFlowSignalsTab` in `role-view.ts`).
 *
 * ── Where this renders (R8) ──────────────────────────────────────────────
 * NOT a sibling above the panel row any more: `FlowShell` hands this element
 * to `FlowPanels`' `header` slot, so it paints INSIDE the Flow column as its
 * first child. The top of the screen is then one continuous 48px band —
 * Chat's `ChatHeaderV2`, this row, and the Actions dock's own strip, all
 * `h-12` + `px-3`, each inside its own column — instead of one full-width
 * bar stacked above three columns. It is hidden with the column, never
 * unmounted (D8), so `GlobalSearch`'s typed draft survives a collapse.
 * Whenever the Actions column is open in push mode (collapsed included —
 * Spencer QA round 3 generalised round 2's collapsed-only rule) the toggles'
 * other host, `RailDock`'s strip, takes over and the search slides into
 * their place at this row's right edge, so the top of the screen is ONE row
 * across Chat · Flow · Actions with the toggles flush with the shell's own
 * top-right corner.
 *
 * ── Wrapping (R11, revised Spencer QA round 3) ───────────────────────────
 * The row is a `@container`. Round 2 gave the search a fixed `w-80` and
 * wrapped it to its own row below `@max-[48rem]`; with all three columns
 * open at a 1280–1440px viewport the Flow column is only ~570–690px, so that
 * wrap fired in the common case and this header was two rows tall beside
 * Chat's and Actions' one — the jagged top line Spencer flagged. Now the
 * search wrapper is `grow basis-0 max-w-80 min-w-0`: it takes up to 320px
 * and SHRINKS below that first (a shrinking input is still a usable input; a
 * second header row is not a straight line). The full-width wrapped row is
 * kept only as the narrow-column floor, below the point where the shrunken
 * search would fall under ~250px: `@max-[44rem]` (704px) while this row also
 * hosts the toggles — fixed content ≈ 274px tabs + 24px padding + 12px gap +
 * 120px cluster (92px + the Trellus headset button, ~28px) + 12px gap ≈ 442px — and `@max-[36rem]` (576px) while it
 * does not (≈ 310px). ARBITRARY container breakpoints, not named ones:
 * Tailwind v4 resolves `@max-md` against `--container-md` (28rem = 448px,
 * `tailwindcss/theme.css`), not the viewport `--breakpoint-md` (Codex round
 * 2). CONTAINER queries, not viewport media queries, because the Flow column
 * can be `FLOW_MIN_PX` (480px) narrow at any viewport once the rep drags a
 * side panel wide. The pixel figures are font-metric estimates pinned by the
 * browser pass, not by a test — jsdom resolves no container query.
 * Precedent for the arbitrary form: `signals/SignalActionCard.tsx`
 * (`@min-[22rem]:`).
 */
function Header({
  subtab,
  setSubtab,
  hiddenTabs = [],
  onSelectAccount,
  onSelectContact,
  initialQuery,
}: HeaderProps) {
  // Spencer QA round 3 — this header hosts the toggles only while it IS the
  // shell's top-right corner (`derivePanelTogglesHost` → 'flow': Actions
  // closed, or overlay mode). With the Actions column open in push mode the
  // dock strip hosts them and the search takes their place at this row's
  // right edge.
  const hostsToggles = usePanelTogglesHost() === 'flow';
  return (
    <div className="@container flex min-h-12 shrink-0 flex-wrap items-center gap-x-3 gap-y-2 border-b-[0.5px] border-border-primary bg-bg-primary px-3">
      {/* `h-12`, not `self-stretch`: once the root wraps to two rows,
          stretching would make the tab row as tall as both. */}
      {subtab !== undefined && setSubtab !== undefined && (
        <Tabs<Subtab> value={subtab} onValueChange={(t) => setSubtab(t)} size="sm" layout="fit" className="h-12">
          <TabList className="h-full">
            {SUBTABS.filter(({ key }) => !hiddenTabs.includes(key)).map(({ key }) => (
              <Tab key={key} value={key}>
                {HEADER_TAB_LABELS[key]}
              </Tab>
            ))}
          </TabList>
        </Tabs>
      )}

      {/* The WRAPPER owns the width (the two class sets above) so
          `GlobalSearch` can be `fullWidth` unconditionally and simply fill
          whatever box it is given. Its `ml-auto` replaces round 2's `flex-1`
          spacer div: it is what pushes the search — and the toggles after
          it — to the right edge once the search has hit its 320px cap. */}
      <div className={hostsToggles ? SEARCH_WRAPPER_BESIDE_TOGGLES : SEARCH_WRAPPER_ALONE}>
        <GlobalSearch
          mode="all"
          activeEntity={subtab === 'account' || subtab === 'contact' ? subtab : 'contact'}
          onSelectAccount={onSelectAccount}
          onSelectContact={onSelectContact}
          initialQuery={initialQuery ?? undefined}
          fullWidth
        />
      </div>

      {hostsToggles && <PanelToggles />}
    </div>
  );
}

export default Header;
